import _ from 'lodash';
import moment from 'moment';
import timeParser from '../../../../../helpers/timeParser';
import dateHelper from '$$dateFormatsHelper';
import customHelper from "$$helpers/custom";
import constants from '$$helpers/constants';

const TASK_ICONS = {
  calendar: {
    icon: 'https://cdn.ganttpro.com/app/imgs/workload/workload-task-tooltip/calendar.svg',
  },
  time: {
    icon: 'https://cdn.ganttpro.com/app/imgs/workload/workload-task-tooltip/timer.svg',
  },
  info: {
    icon: 'https://cdn.ganttpro.com/app/imgs/workload/workload-task-tooltip/info.svg',
  },
};

const PRIORITY_MAP = {
  5: {
    value: __('filter_priority_highest'),
    icon: 'https://cdn.ganttpro.com/app/imgs/highest.svg',
  },
  6: {
    value: __('filter_priority_high'),
    icon: 'https://cdn.ganttpro.com/app/imgs/high.svg',
  },
  7: {
    value: __('filter_priority_medium'),
    icon: 'https://cdn.ganttpro.com/app/imgs/medium.svg',
  },
  8: {
    value: __('filter_priority_low'),
    icon: 'https://cdn.ganttpro.com/app/imgs/low.svg',
  },
  9: {
    value: __('filter_priority_lowest'),
    icon: 'https://cdn.ganttpro.com/app/imgs/lowest.svg',
  },
};

const STATUS_MAP = {
  1: {
    value: __('filter_status_open'),
    color: '#9b9b9b',
  },
  2: {
    value: __('filter_status_in_progress'),
    color: '#ff9921',
  },
  3: {
    value: __('filter_status_done'),
    color: '#55BED4',
  },
  4: {
    value: __('filter_status_closed'),
    color: '#14bb4c',
  },
};

class Tooltip {
  constructor() {
    this.node = null;
  }

  init(instance, config) {
    this.ganttInstance = instance;
    this.config = config;
  }

  showResourceCellTooltip(event, totalTasksLength, totalHours, workHours, projectsData) {
    if (!totalHours || !event.target.offsetHeight) {
      return;
    }

    let dataFromInaccessibleProjects = null;

    if (!this.node) {
      this.node = document.createElement('div');

      this.node.classList.add('workload-tooltip');

      document.body.appendChild(this.node);
    }

    this.node.innerHTML = `
      <div class='workload-tooltip-head'>
        <div class='workload-tooltip-head-load'>
          ${__('workload_loaded_label')}
          <span>${`${totalHours}/${workHours}`}</span>
          ${__('workload_avaliable_label')}
        </div>
        <div class='workload-tooltip-head-load'>${__('workload_task_assigned_label')}: <span>${totalTasksLength}</span></div>
      </div>
      <div class='workload-tooltip-projects'>`;

    _.each(projectsData, (data, projectId) => {
      const project = this.ganttInstance.ganttDataApi.getProjectItem(+projectId);

      if (!project) {
        if (!dataFromInaccessibleProjects) {
          dataFromInaccessibleProjects = {
            hours: 0,
            tasksCount: 0,
          };
        }
        dataFromInaccessibleProjects.hours += _.floor(data.hours, 2);
        dataFromInaccessibleProjects.tasksCount += data.tasks.length;

        return;
      }

      this.node.innerHTML += `
        <div class='workload-tooltip-projects-item'>
          <div class='workload-tooltip-projects-item-name'>${project.name}</div>
          <div class='workload-tooltip-projects-item-info'>
            ${__('tasks')}: ${data.tasks.length} / ${__('hours')}: ${_.floor(data.hours, 2)}
          </div>
        </div>`;
    });

    if (dataFromInaccessibleProjects) {
      this.node.innerHTML += `
        <div class='workload-tooltip-projects-item'>
          <div class='workload-tooltip-projects-item-name'>${__('workload_from_inaccessible_projects')}</div>
          <div class='workload-tooltip-projects-item-info'>
            ${__('tasks')}: ${dataFromInaccessibleProjects.tasksCount} / ${__('hours')}: ${_.floor(dataFromInaccessibleProjects.hours, 2)}
          </div>
        </div>`;
    }

    this.node.innerHTML += '</div>';

    const pos = this._calculatePosition(this.node, event, this.config.ROW_HEIGHT);

    this.node.style.top = `${pos.top}px`;
    this.node.style.left = `${pos.left}px`;
  }

  showTaskBarTooltip(taskData, targetNode, event) {
    if (!event.target.offsetHeight) {
      return;
    }

    if (!this.node) {
      this.node = document.createElement('div');

      this.node.classList.add('workload-tooltip-task');

      document.body.appendChild(this.node);
    }

    this.node.innerHTML = this._getTaskTooltipTemplate(taskData);
    this.node.innerHTML = this.node.innerHTML.replace(/&nbsp;/gi, ' ');

    const pos = this._calculatePosition(this.node, event, this.config.RESOURCE_TASK_HEIGHT);

    this.node.style.top = `${pos.top}px`;
    this.node.style.left = `${pos.left}px`;
  }

  hideTooltip() {
    if (this.node) {
      document.body.removeChild(this.node);
      this.node = null;
    }
  }

  _calculatePosition(tooltipNode, event, topDelta) {
    let topPosition = event.clientY - tooltipNode.offsetHeight - event.offsetY - 5;
    let leftPosition = event.clientX - tooltipNode.offsetWidth / 2;
    const documentWidth = document.documentElement.clientWidth;
    const documentHeight = document.documentElement.clientHeight;

    if (documentWidth <= leftPosition + tooltipNode.offsetWidth) {
      leftPosition = documentWidth - tooltipNode.offsetWidth - 5;
    }

    if (topPosition <= 0) {
      topPosition = event.clientY + topDelta - event.offsetY + 5;

      if ((documentHeight - topPosition) < tooltipNode.offsetHeight) {
        topPosition = 5;
        leftPosition = event.clientX - tooltipNode.offsetWidth - event.offsetX - 5;
      }
    }

    return {
      top: topPosition,
      left: leftPosition,
    };
  }

  _getTaskTooltipTemplate(taskData) {
    const breadCrumbsTasks = this.ganttInstance.ganttDataApi.getTasksDataForBreadcrumbs(taskData.id, taskData.gantt_id);
    const projectData = this.ganttInstance.ganttDataApi.getProjectItem(taskData.gantt_id);
    const projectDataText = projectData.name.replace(/&nbsp;/gi, ' ');
    const breadcrumbsText = breadCrumbsTasks.reduce((sum, elem) => `${sum} / ${elem.text}`, '');
    const startDateText = this.ganttInstance.date.date_to_str(dateHelper.getDateFormat())(moment(taskData.start_date).toDate());
    const endDateText = this.ganttInstance.date.date_to_str(dateHelper.getDateFormat())(moment(taskData.end_date).toDate());
    const duration = this.ganttInstance.calculateDuration(new Date(taskData.start_date), new Date(taskData.end_date), taskData);
    let durationText = timeParser.output(duration, {
      prop: 'duration',
      durationData: this.ganttInstance.config.durationData, // TODO
      durationStep: this.ganttInstance.config.duration_view,
    });
    const durationStep = durationText[durationText.length - 1];
    let estimationText = timeParser.output(taskData.estimation, { prop: 'estimation' });
    const estimationStep = estimationText[estimationText.length - 1];
    const progress = parseFloat(taskData.progress * 100).toFixed(0);

    durationText = durationText.slice(0, durationText.length - 1);
    estimationText = estimationText.slice(0, estimationText.length - 1);

    const timeSection = `<div class="section">
      <span class="section_icon">
        <img src="${TASK_ICONS.time.icon}" alt="time">
      </span>
      <div class='section_item'>
        <span class="section_item-text">${__('gantt_grid_duration')}:</span>
        <span class="section_item-accent">${durationText}</span>
        <span class="section_item-desc">${durationStep}</span>
      </div>
      <div class='section_item'>
        <span class="section_item-text">${__('gantt_grid_estimation')}:</span>
        <span class="section_item-accent">${estimationText}</span>
        <span class="section_item-desc">${estimationStep}</span>
      </div>
    </div>`;

    let statusColor;
    let statusValue;
    let priorityIcon;
    let priorityValue;

    if (projectData.is_jira) {
      ({
        statusColor,
        statusValue,
        priorityIcon,
        priorityValue,
      } = this._getStatusAndPriorityPropsForJiraTask(taskData));
    } else {
      statusColor = STATUS_MAP[taskData.status].color;
      statusValue = STATUS_MAP[taskData.status].value;

      priorityIcon = PRIORITY_MAP[taskData.priority].icon;
      priorityValue = PRIORITY_MAP[taskData.priority].value;
    }

    const html = `
        <div class='head'>
          <div class='name'>${customHelper.formatTaskName(taskData.text)}</div>
          <div class='breadcrumbs'>${customHelper.formatTaskName((projectDataText + breadcrumbsText))}</div>
        </div>
        <div class='body'>
          <div class="section">
            <span class="section_icon">
              <img src="${TASK_ICONS.calendar.icon}" alt="calendar">
            </span>
            <div class='section_item'>
              <span class="section_item-text">${__('gantt_grid_start_date')}:</span>
              <span class="section_item-accent">${startDateText}</span>
            </div>
            <div class='section_item'>
              <span class="section_item-text">${__('gantt_grid_end_date')}:</span>
              <span class="section_item-accent">${endDateText}</span>
            </div>
          </div>
          ${taskData.type === constants.TASK_TYPES.task ? timeSection : ''}
          <div class="section">
            <span class="section_icon">
              <img src="${TASK_ICONS.info.icon}" alt="info">
            </span>
            <div class='section_item'>
              <span class="section_item-text">${__('gantt_grid_status')}:</span>
              <span class="section_item-circle" style="background: ${statusColor}"></span>
              <span class="bold">${statusValue}</span>
            </div>
            <div class='section_item'>
              <span class="section_item-text">${__('gantt_grid_progress')}:</span>
              <span class="section_item-accent">${progress}</span>%
            </div>
            <div class='section_item'>
              <span class="section_item-text">${__('gantt_grid_priority')}:</span>
              <span class="item_icon priority" style="background-image: url(${priorityIcon})"></span>
              <span class="bold">${priorityValue}</span>
            </div>
          </div>
        </div>`;

    return html;
  }

  _getStatusAndPriorityPropsForJiraTask(taskData) {
    const jiraCustomColumns = this.ganttInstance.ganttDataApi.getJiraColumnsByProjectId(taskData.gantt_id);

    let priorityColumn;
    let statusColumn;

    jiraCustomColumns.some(column => {
      if (column.name === 'priority') priorityColumn = column;
      if (column.name === 'status') statusColumn = column;

      return priorityColumn && statusColumn;
    });

    const status = statusColumn.options.find(({ id }) => id === taskData.status);
    const statusColor = status.color;
    const statusValue = status.value;

    const priority = priorityColumn.options.find(({ id }) => id === taskData.priority);
    const priorityIcon = priority.icon;
    const priorityValue = priority.value;

    return {
      statusColor,
      statusValue,
      priorityIcon,
      priorityValue,
    };
  }
}

export default new Tooltip();
